import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export interface ShipperBid {
  id: string;
  order_id: string;
  transporter_user_id: string;
  transporter_company_id: string;
  amount: number;
  message: string | null;
  is_accepted: boolean;
  declined_at: string | null;
  created_at: string;
  order: {
    id: string;
    order_number: string;
    cargo_title: string | null;
    cargo_description: string;
    pickup_address: string | null;
    delivery_address: string | null;
    pickup_date: string;
    delivery_date: string;
  };
  transporter_company: {
    id: string;
    name: string;
    email: string;
    phone: string | null;
  };
}

export const useShipperBids = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Fetch pending bids for shipper's orders
  const { data: pendingBids = [], isLoading } = useQuery({
    queryKey: ['shipper-bids'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Récupérer le company_id de l'utilisateur
      const { data: userData } = await supabase
        .from('users')
        .select('company_id')
        .eq('id', user.id)
        .single();

      const companyId = userData?.company_id;

      // Construire la requête
      let query = supabase
        .from('bids')
        .select(`
          *,
          order:orders!inner(
            id,
            order_number,
            cargo_title,
            cargo_description,
            pickup_address,
            delivery_address,
            pickup_date,
            delivery_date,
            shipper_user_id,
            shipper_company_id
          ),
          transporter_company:companies!bids_transporter_company_id_fkey(
            id,
            name,
            email,
            phone
          )
        `)
        .is('is_accepted', false)
        .is('declined_at', null);

      // Appliquer le filtre selon les données disponibles
      if (companyId) {
        query = query.or(`shipper_user_id.eq.${user.id},shipper_company_id.eq.${companyId}`, { foreignTable: 'orders' });
      } else {
        query = query.eq('orders.shipper_user_id', user.id);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      return data as ShipperBid[];
    },
  });

  // Accept a bid
  const acceptBidMutation = useMutation({
    mutationFn: async (bidId: string) => {
      // Get bid details first
      const { data: bid, error: bidError } = await supabase
        .from('bids')
        .select('*, order_id, amount, transporter_company_id')
        .eq('id', bidId)
        .single();

      if (bidError) throw bidError;

      // Update bid as accepted
      const { error: updateBidError } = await supabase
        .from('bids')
        .update({ is_accepted: true })
        .eq('id', bidId);

      if (updateBidError) throw updateBidError;

      // Decline all other bids for this order
      const { error: declineOthersError } = await supabase
        .from('bids')
        .update({ declined_at: new Date().toISOString() })
        .eq('order_id', bid.order_id)
        .neq('id', bidId)
        .is('is_accepted', false)
        .is('declined_at', null);

      if (declineOthersError) throw declineOthersError;

      // Update order final price
      const { error: updateOrderError } = await supabase
        .from('orders')
        .update({
          final_price: bid.amount,
        })
        .eq('id', bid.order_id);

      if (updateOrderError) throw updateOrderError;

      // Create transport_job for the transporter
      const { error: createJobError } = await supabase
        .from('transport_jobs')
        .insert({
          order_id: bid.order_id,
          transporter_company_id: bid.transporter_company_id,
          booked_at: new Date().toISOString(),
          accepted_at: new Date().toISOString(), // Accepté immédiatement
        });

      if (createJobError) throw createJobError;

      return bid;
    },
    onSuccess: () => {
      toast.success(t('shipper.messages.bidAccepted'));
      queryClient.invalidateQueries({ queryKey: ['shipper-bids'] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || t('shipper.errors.acceptBidError'));
    },
  });

  // Decline a bid
  const declineBidMutation = useMutation({
    mutationFn: async (bidId: string) => {
      const { error } = await supabase
        .from('bids')
        .update({ declined_at: new Date().toISOString() })
        .eq('id', bidId);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(t('shipper.messages.bidDeclined'));
      queryClient.invalidateQueries({ queryKey: ['shipper-bids'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || t('shipper.errors.declineBidError'));
    },
  });

  return {
    pendingBids,
    isLoading,
    acceptBid: acceptBidMutation.mutateAsync,
    declineBid: declineBidMutation.mutateAsync,
    isAccepting: acceptBidMutation.isPending,
    isDeclining: declineBidMutation.isPending,
  };
};
